// The cut editor (ADR-0058): step through a clip frame by frame, mark the
// first and last frame to keep, preview the kept part on a loop, and save
// it as a new clip. The cut runs in this browser (lib/videoCut.ts); the
// result is uploaded like a photo, labelled "<source> · cut a–b", with
// attrs naming the source and the frames, so the Library's Results show it
// beside the clip it came from. The source clip is never changed.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { allocateArtifact, getArtifact, type ArtifactView, type MediaInfo } from "../api/client";
import { fetchStoreBlob, storeSrc, uploadToStore } from "../api/store";
import { DEFAULT_FPS } from "../lib/compare";
import {
  canCut,
  cutMeta,
  cutVideo,
  formatClock,
  frameAt,
  frameCount,
  keptFrames,
  timeOfFrame,
  validCut,
  type CutRange,
} from "../lib/videoCut";

type SaveState = { phase: "idle" } | { phase: "working"; step: string; fraction: number } | { phase: "saved"; id: string };

export default function Cut() {
  const { id = "" } = useParams();
  const [view, setView] = useState<ArtifactView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [frames, setFrames] = useState(0);
  const [frame, setFrame] = useState(0);
  const [range, setRange] = useState<CutRange>({ from: 0, to: 0 });
  const [playing, setPlaying] = useState(false);
  const [looping, setLooping] = useState(false);
  const [supported, setSupported] = useState<boolean | null>(null);
  const [save, setSave] = useState<SaveState>({ phase: "idle" });
  const videoRef = useRef<HTMLVideoElement>(null);

  const media = view?.media as MediaInfo | undefined;
  const fps = media?.fps && media.fps > 0 ? media.fps : DEFAULT_FPS;
  const src = useMemo(() => storeSrc(view), [view]);

  useEffect(() => {
    let live = true;
    getArtifact(id)
      .then((v) => {
        if (!live) return;
        setView(v);
        const m = v.media as MediaInfo | undefined;
        const n = frameCount(m?.duration_s, m?.fps);
        if (n > 0) {
          setFrames(n);
          setRange({ from: 0, to: n - 1 });
        }
        const w = m?.width ?? 0;
        const h = m?.height ?? 0;
        if (w > 0 && h > 0) {
          canCut(w, h)
            .then((ok) => live && setSupported(ok))
            .catch(() => live && setSupported(false));
        }
      })
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [id]);

  // A clip Store has not probed yet: count frames from what the player reports.
  const onMetadata = () => {
    const v = videoRef.current;
    if (!v || frames > 0 || !Number.isFinite(v.duration)) return;
    const n = frameCount(v.duration, fps);
    setFrames(n);
    setRange({ from: 0, to: n - 1 });
  };

  const seekFrame = useCallback(
    (k: number) => {
      const v = videoRef.current;
      const clamped = Math.max(0, Math.min(frames - 1, k));
      setFrame(clamped);
      if (v) v.currentTime = timeOfFrame(clamped, fps);
    },
    [frames, fps],
  );

  const pause = useCallback(() => {
    videoRef.current?.pause();
    setPlaying(false);
    setLooping(false);
  }, []);

  const play = useCallback((loopKept: boolean) => {
    const v = videoRef.current;
    if (!v) return;
    if (loopKept) v.currentTime = timeOfFrame(range.from, fps);
    setLooping(loopKept);
    setPlaying(true);
    void v.play()?.catch(() => setPlaying(false));
  }, [range.from, fps]);

  const onTime = () => {
    const v = videoRef.current;
    if (!v) return;
    const k = frameAt(v.currentTime, fps, frames);
    if (looping && (k > range.to || k < range.from)) {
      v.currentTime = timeOfFrame(range.from, fps);
      setFrame(range.from);
      return;
    }
    setFrame(k);
  };

  const setStart = (k: number) => setRange((r) => ({ from: Math.min(k, r.to - 1), to: r.to }));
  const setEnd = (k: number) => setRange((r) => ({ from: r.from, to: Math.max(k, r.from + 1) }));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        pause();
        seekFrame(frame + (e.key === "ArrowLeft" ? -1 : 1));
      } else if (e.key === "i" || e.key === "I") {
        setStart(frame);
      } else if (e.key === "o" || e.key === "O") {
        setEnd(frame);
      } else if (e.key === " ") {
        e.preventDefault();
        if (playing) pause();
        else play(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [frame, playing, pause, play, seekFrame]);

  const ok = frames > 0 && validCut(range, frames);
  const busy = save.phase === "working";

  async function onSave() {
    if (!view?.download_url || !view.download_token || !ok) return;
    pause();
    setError(null);
    try {
      setSave({ phase: "working", step: "Downloading the clip", fraction: 0 });
      const source = await fetchStoreBlob(view.download_url, view.download_token);
      setSave({ phase: "working", step: "Cutting", fraction: 0 });
      const out = await cutVideo(source, range, fps, (f) => setSave({ phase: "working", step: "Cutting", fraction: f }));
      setSave({ phase: "working", step: "Uploading", fraction: 1 });
      const meta = cutMeta({ id: view.id, label: view.label }, range);
      const alloc = await allocateArtifact("video/mp4", out.size, meta);
      await uploadToStore(alloc.upload_url, alloc.upload_token, out, "video/mp4");
      setSave({ phase: "saved", id: alloc.artifact_id });
    } catch (e) {
      setSave({ phase: "idle" });
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const pct = (k: number) => (frames > 1 ? (k / (frames - 1)) * 100 : 0);

  return (
    <div className="u-page u-cut">
      <div className="u-head">
        <h1 className="u-h1">Cut</h1>
        <p className="u-lede">Keep the frames you want; the original stays as it is.</p>
        <Link className="u-btn u-btn--quiet" to="/library">
          Back to Library
        </Link>
      </div>
      {error && <p className="u-error">{error}</p>}
      {supported === false && (
        <p className="u-error" data-testid="cut-unsupported">
          This browser cannot encode H.264 video at this size, so it cannot save a cut. Chrome, Edge and Safari can.
        </p>
      )}
      {view && (
        <>
          {view.label && <p className="u-note">{view.label}</p>}
          <video
            ref={videoRef}
            className="u-cut__video"
            src={src}
            playsInline
            preload="auto"
            onLoadedMetadata={onMetadata}
            onTimeUpdate={onTime}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
          />
          <div className="u-cut__timeline" aria-hidden="true">
            <span
              className="u-cut__kept"
              style={{ left: `${pct(range.from)}%`, width: `${Math.max(0, pct(range.to) - pct(range.from))}%` }}
            />
            <span className="u-cut__head" style={{ left: `${pct(frame)}%` }} />
          </div>
          <input
            className="u-cut__seek"
            type="range"
            aria-label="Frame"
            min={0}
            max={Math.max(0, frames - 1)}
            step={1}
            value={frame}
            onChange={(e) => {
              pause();
              seekFrame(Number(e.target.value));
            }}
          />
          <div className="u-cut__controls">
            <button className="u-btn" onClick={() => (playing ? pause() : play(false))}>
              {playing ? "Pause" : "Play"}
            </button>
            <button className="u-btn u-btn--quiet" onClick={() => { pause(); seekFrame(frame - 1); }} title="Previous frame (←)">
              ◀ frame
            </button>
            <button className="u-btn u-btn--quiet" onClick={() => { pause(); seekFrame(frame + 1); }} title="Next frame (→)">
              frame ▶
            </button>
            <span className="u-cut__clock" data-testid="cut-clock">
              frame {frame} / {Math.max(0, frames - 1)} · {formatClock(frame / fps)}
            </span>
          </div>
          <div className="u-cut__controls">
            <button className="u-btn" onClick={() => setStart(frame)} title="Keep from this frame (I)">
              Start here
            </button>
            <button className="u-btn" onClick={() => setEnd(frame)} title="Keep up to this frame (O)">
              End here
            </button>
            <label className="u-field u-field--row">
              First frame
              <input
                className="u-input u-cut__num"
                type="number"
                min={0}
                max={Math.max(0, frames - 2)}
                value={range.from}
                onChange={(e) => setRange((r) => ({ ...r, from: Math.trunc(Number(e.target.value)) }))}
              />
            </label>
            <label className="u-field u-field--row">
              Last frame
              <input
                className="u-input u-cut__num"
                type="number"
                min={1}
                max={Math.max(0, frames - 1)}
                value={range.to}
                onChange={(e) => setRange((r) => ({ ...r, to: Math.trunc(Number(e.target.value)) }))}
              />
            </label>
            <button className="u-btn u-btn--quiet" onClick={() => play(true)} disabled={!ok}>
              Play kept part
            </button>
          </div>
          <p className="u-note" data-testid="cut-summary">
            Keeps frames {range.from}–{range.to}: {keptFrames(range)} of {frames} ({formatClock(keptFrames(range) / fps)} of{" "}
            {formatClock(frames / fps)}). ← → step a frame, I / O set start and end, space plays.
          </p>
          <div className="u-cut__controls">
            <button className="u-btn u-btn--solid" onClick={() => void onSave()} disabled={!ok || busy || supported === false}>
              {busy ? "Saving…" : "Save cut"}
            </button>
            {save.phase === "working" && (
              <span className="u-note" data-testid="cut-progress">
                {save.step}
                {save.step === "Cutting" ? ` ${Math.round(save.fraction * 100)}%` : "…"}
              </span>
            )}
            {save.phase === "saved" && (
              <span className="u-note" data-testid="cut-saved">
                Saved as a new clip. <Link to="/library">Open the Library</Link>
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
